import { useTranslation } from "react-i18next";
import { Eye, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { EmailDraft } from "@/lib/emailApi";

const LIMITS = { subject: 150, title: 120, message: 5000, cta_label: 40, cta_url: 500 } as const;
const FIELD = "field-premium w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground focus:outline-none";

interface Props {
  draft: EmailDraft;
  counts: { SERVICE: number; MARKETING: number } | null;
  busy: boolean;
  onChange: (d: EmailDraft) => void;
  onPreview: () => void;
}

export function EmailComposeForm({ draft, counts, busy, onChange, onPreview }: Props) {
  const { t } = useTranslation();
  const set = (patch: Partial<EmailDraft>) => onChange({ ...draft, ...patch });
  const tpl = !!draft.template_code;
  const canPreview = (tpl || (draft.subject.trim() && draft.message.trim())) && (draft.target_type === "bulk" || (draft.to || "").trim());
  const Toggle = ({ field, values }: { field: "category" | "target_type"; values: readonly string[] }) => (
    <div className="flex gap-2" role="radiogroup">
      {values.map((v) => (
        <button key={v} type="button" role="radio" aria-checked={draft[field] === v} onClick={() => set({ [field]: v } as Partial<EmailDraft>)}
          data-testid={`admin-email-${field}-${v}`}
          className={cn("rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors",
            draft[field] === v ? "border-cyan-300 bg-cyan-300/15 text-cyan-200" : "border-white/15 text-muted-foreground hover:border-white/40")}>
          {t(`adminEmail.${field}.${v}`)}
        </button>
      ))}
    </div>
  );
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); if (canPreview && !busy) onPreview(); }} data-testid="admin-email-form">
      {tpl && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-cyan-300/40 bg-cyan-300/10 px-4 py-2 text-sm text-cyan-100" data-testid="admin-email-template-mode">
          <span>{t("adminEmail.official.mode", { name: t(`adminEmail.official.names.${draft.template_code}`) })}</span>
          <button type="button" onClick={() => set({ template_code: null, subject: "" })} data-testid="admin-email-template-mode-exit" className="text-xs underline">{t("adminEmail.official.exitMode")}</button>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {!tpl && <div className="space-y-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><p>{t("adminEmail.categoryLabel")}</p><Toggle field="category" values={["SERVICE", "MARKETING"]} /></div>}
        <div className="space-y-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><p>{t("adminEmail.targetLabel")}</p><Toggle field="target_type" values={["single", "bulk"]} /></div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="admin-email-category-hint">{t(`adminEmail.hint.${draft.category}`)}</p>
      {draft.target_type === "single" ? (
        <input type="email" value={draft.to || ""} onChange={(e) => set({ to: e.target.value })} placeholder={t("adminEmail.toPlaceholder")} className={FIELD} data-testid="admin-email-to" />
      ) : (
        <p className="rounded-xl border border-cyan-300/30 bg-cyan-300/5 px-4 py-2 text-sm text-cyan-100" data-testid="admin-email-bulk-count">
          {t("adminEmail.bulkCount", { count: counts ? counts[draft.category] : 0 })}
        </p>
      )}
      {!tpl && <>
      <input value={draft.subject} maxLength={LIMITS.subject} onChange={(e) => set({ subject: e.target.value })} placeholder={t("adminEmail.subject")} className={FIELD} data-testid="admin-email-subject" />
      <input value={draft.title || ""} maxLength={LIMITS.title} onChange={(e) => set({ title: e.target.value })} placeholder={t("adminEmail.titleOpt")} className={FIELD} data-testid="admin-email-title" />
      <textarea value={draft.message} maxLength={LIMITS.message} rows={7} onChange={(e) => set({ message: e.target.value })} placeholder={t("adminEmail.message")} className={FIELD} data-testid="admin-email-message" />
      <div className="grid gap-4 sm:grid-cols-2">
        <input value={draft.cta_label || ""} maxLength={LIMITS.cta_label} onChange={(e) => set({ cta_label: e.target.value })} placeholder={t("adminEmail.ctaLabel")} className={FIELD} data-testid="admin-email-cta-label" />
        <input value={draft.cta_url || ""} maxLength={LIMITS.cta_url} onChange={(e) => set({ cta_url: e.target.value })} placeholder="https://" className={FIELD} data-testid="admin-email-cta-url" />
      </div>
      </>}
      {tpl && <p className="text-sm text-white/80" data-testid="admin-email-template-subject"><span className="text-xs uppercase tracking-widest text-muted-foreground">{t("adminEmail.subjectShort")}:</span> {draft.subject}</p>}
      <button type="submit" disabled={!canPreview || busy} data-testid="admin-email-preview-btn" className="cta-hero inline-flex items-center gap-2 px-6 py-2.5 text-sm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}{t("adminEmail.previewBtn")}
      </button>
    </form>
  );
}
